import { isNil, isNotNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { z } from 'zod';

import { parseNullableValue } from '@hrlogics/helper-utils';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  EmployerListItemForVerificationRequestInfoFragment,
  EmployerStaffRole,
  EmployerStaffWhere,
  EmployerTableItemFragment,
  EmployerWhere,
  QueryMode,
  UserStatus,
  VerificationRequestCompanyData,
} from 'src/api';
import { COMPANY_SEARCH_CATEGORY_MAP } from 'src/constants/company';

import { getEmailDomainForSearch } from './email';
import { formatFeinNumber } from './fein';
import { buildUrl } from './router';

type EmployersTableFilter = {
  status?: Nullable<UserStatus[]>;
};

const getEmployerAdmins = (
  employerStaffs: EmployerTableItemFragment['employerStaffsList'],
): EmployerTableItemFragment['employerStaffsList'] => {
  return employerStaffs.filter(staffUser => staffUser.role === EmployerStaffRole.EmployerAdmin);
};

const determineEmployerAdminsStatus = (
  employerAdmins: EmployerTableItemFragment['employerStaffsList'],
): UserStatus => {
  if (isEmpty(employerAdmins)) {
    return UserStatus.Inactive;
  }

  if (employerAdmins.some(employerAdmin => employerAdmin.user.status === UserStatus.Active)) {
    return UserStatus.Active;
  }

  if (employerAdmins.every(employerAdmin => employerAdmin.user.status === UserStatus.Pending)) {
    return UserStatus.Pending;
  }

  return UserStatus.Inactive;
};

export const getEmployerStatus = (employer: EmployerTableItemFragment): UserStatus => {
  const employerAdmins = getEmployerAdmins(employer.employerStaffsList);
  return determineEmployerAdminsStatus(employerAdmins);
};

export const prepareEmployersFilterByStatus = (
  filter: EmployersTableFilter,
): EmployerWhere | undefined => {
  if (isNil(filter.status) || isEmpty(filter.status)) return undefined;

  const hasActiveAdmin = {
    employerStaffs: {
      some: {
        role: { equals: EmployerStaffRole.EmployerAdmin },
        user: { status: { equals: UserStatus.Active } },
      },
    },
  } satisfies EmployerWhere;

  const hasPendingAdmin = {
    employerStaffs: {
      some: {
        role: { equals: EmployerStaffRole.EmployerAdmin },
        user: { status: { equals: UserStatus.Pending } },
      },
    },
  } satisfies EmployerWhere;

  const hasInactiveAdmin = {
    employerStaffs: {
      some: {
        role: { equals: EmployerStaffRole.EmployerAdmin },
        user: { status: { equals: UserStatus.Inactive } },
      },
    },
  } satisfies EmployerWhere;

  const hasAnyAdmin = {
    employerStaffs: {
      some: {
        role: { equals: EmployerStaffRole.EmployerAdmin },
      } satisfies EmployerStaffWhere,
    },
  } satisfies EmployerWhere;

  return {
    OR: filter.status.map(status => {
      // Active: at least one Admin with Active status
      if (status === UserStatus.Active) {
        return hasActiveAdmin;
      }

      // Pending: no Active admins, all admins are Pending (at least one exists)
      if (status === UserStatus.Pending) {
        return {
          AND: [{ NOT: [hasActiveAdmin] }, hasPendingAdmin, { NOT: [hasInactiveAdmin] }],
        };
      }

      // Inactive: no Active admins AND (no admins at all OR at least one Inactive admin)
      return {
        AND: [
          { NOT: [hasActiveAdmin] },
          {
            OR: [{ NOT: [hasAnyAdmin] }, hasInactiveAdmin],
          },
        ],
      };
    }),
  };
};

export const buildEmployerDetailsUrl = (employerId: string) => {
  return buildUrl(appRoutes.employers.detail.companyInformation, {
    [RouterEntitiesId.employer]: employerId,
  });
};

export const prepareEmployerSearchFilterByCategories = (
  searchValue: Nullable<string>,
  searchCategory: Nullable<string>,
): EmployerWhere | undefined => {
  if (!searchValue) return undefined;
  if (!searchCategory) searchCategory = COMPANY_SEARCH_CATEGORY_MAP.displayName;

  if (searchCategory === COMPANY_SEARCH_CATEGORY_MAP.displayName) {
    return {
      displayName: {
        contains: searchValue,
        mode: QueryMode.Insensitive,
      },
    };
  }

  if (searchCategory === COMPANY_SEARCH_CATEGORY_MAP.staffEmail) {
    const { success, data: staffEmail } = z.string().trim().email().safeParse(searchValue);
    if (!success || !staffEmail) return {};

    return {
      employerStaffs: {
        some: {
          user: {
            email: { equals: staffEmail.toLowerCase() },
          },
        },
      },
    };
  }

  if (searchCategory === COMPANY_SEARCH_CATEGORY_MAP.emailDomain) {
    const emailDomain = getEmailDomainForSearch(searchValue);
    if (!emailDomain) return {};

    return {
      OR: [
        { contactEmail: { endsWith: emailDomain.toLowerCase() } },
        {
          employerStaffs: {
            some: {
              user: { email: { endsWith: emailDomain.toLowerCase() } },
            },
          },
        },
      ],
    };
  }

  return undefined;
};

export const buildEmployersListForVerificationRequest = (
  employersList: EmployerListItemForVerificationRequestInfoFragment[] | undefined,
): SelectFieldOption<EmployerListItemForVerificationRequestInfoFragment>[] => {
  if (!employersList) return [];
  return employersList.map(employer => {
    const formattedFein = formatFeinNumber(employer.feinValue);
    return {
      value: employer,
      label: [
        parseNullableValue(employer.displayName),
        formattedFein ? `FEIN: ${formattedFein}` : null,
      ]
        .filter(isNotNil)
        .join(' - '),
    };
  });
};

export const convertEmployerListItemToEmployerDataItem = (
  employerListItem: EmployerListItemForVerificationRequestInfoFragment,
): VerificationRequestCompanyData => {
  return {
    id: employerListItem.id,
    employeeId: employerListItem.employeeId,
    topLevelCompanyId: employerListItem.topLevelCompanyId,
    checksum: employerListItem.checksum,
  };
};
export const convertEmployerListItemsToEmployerDataItems = (
  employerListItems: EmployerListItemForVerificationRequestInfoFragment[],
): VerificationRequestCompanyData[] => {
  return employerListItems.map(convertEmployerListItemToEmployerDataItem);
};
